/* eslint-disable no-console */
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import getConfig from 'next/config'
import absoluteUrl from 'next-absolute-url'
import { BreadcrumbJsonLd } from 'next-seo'
import { getCategoryContent, getCategorySlugs } from '@/fetchers'
import { ErrorPage } from '@/components'
import Head from '@/components/DocumentHead'
import {
  checkIsErrorPage,
  checkIsPreviewModeRequest,
  getPageSlug,
  getCategoryPageId,
  getLatestArticles,
  getLatestTopicTagRelatedArticles,
} from '@/utils'
import { DOMAIN_LOCALES, Locales } from '@/interfaces/locales'
import { DEVELOPMENT_HOST } from '@/constants/app.constants'
import Category from '@/components/Category'
import { CategoryTypeKeys } from '@/interfaces/category'
import { ILanguageFields } from '@/interfaces/head'
import { IPmArticle } from '@/interfaces/article'
import {
  NUM_OF_PM_ARTICLES,
  FIRST_THUMBNAIL_LIST_COUNT_DESKTOP,
  SECOND_THUMBNAIL_LIST_COUNT_DESKTOP,
} from '@/constants'
import { getPmArticles } from '@/fetchers'

const { serverRuntimeConfig } = getConfig()

const CategoryPage: React.FC<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = ({
  data,
  languageData,
  status,
  currentDomain,
  latestArticles,
  pmArticles,
  isRedis,
  locale,
  lang,
  latestArticlesLength,
}) => {
  // eslint-disable-next-line no-console
  if (isRedis) console.log('v.2.1')

  if (status) return <ErrorPage code={status} domain={currentDomain} />
  if (!data) return <ErrorPage code="404" domain={currentDomain} />

  const { webDomain } = getConfig().publicRuntimeConfig

  return (
    <>
      <Head data={data.seo} languageData={languageData} />
      <BreadcrumbJsonLd itemListElements={[
        {
          position: 1,
          name: 'Homepage',
          item: `https://${webDomain}/`,
        },
        {
          position: 2,
          name: 'Insights',
          item: `https://${webDomain}/insights`,
        },
      ]} />
      <Category
        {...data}
        pmArticles={pmArticles}
        articles={latestArticles} 
        locale={locale}
        lang={lang}
        latestArticlesLength={latestArticlesLength}
      />
    </>
  )
}

interface PageProps {
  data: any | null //TODO: update the fetcher to not return <any>, and update any here
  languageData: ILanguageFields[] | null
  currentDomain: string
  latestArticles: any | null //TODO: update the fetcher to not return <any>, and update any here
  pmArticles: IPmArticle[] | null
  isRedis: boolean
  locale: Locales
  lang: string
  latestArticlesLength: number
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  let getServerPropsCached: (pageURL: string) => Promise<string> = (() => null) as any
  let cacheServerProps: (pageURL: string, data: Record<string, any>) => void = () => null
  let setUrlById: (sysId: string, pageURL: string) => void = () => null

  if (!serverRuntimeConfig.useDrug) {
    const redisUtils = await import('@/utils/redis.utils')

    getServerPropsCached = redisUtils.getServerPropsCached
    cacheServerProps = redisUtils.cacheServerProps
    setUrlById = redisUtils.setUrlById
  }

  const { host } = absoluteUrl(ctx.req)
  const { publicRuntimeConfig } = getConfig()
  const currentDomain = host === DEVELOPMENT_HOST ? publicRuntimeConfig.webDomain : host
  const isPreviewMode = checkIsPreviewModeRequest(ctx.query)

  ctx.res.setHeader(
    'Cache-Control',
    'public, s-maxage=43200, stale-while-revalidate=60'
  )
  
  const pageSlug = getPageSlug(ctx.query.slug, ctx.resolvedUrl?.split('?')[0], ctx.query)
  const articleLang = !Array.isArray(ctx.query.lang) ? ctx.query.lang || '' : ''
  const categorySlug = articleLang ? `/${articleLang}` : pageSlug
  const categoryPageId = getCategoryPageId(pageSlug)
  const combinedPageSlug = '/insights' + (categorySlug === '/' ? '' : categorySlug)

  const basePageProps = {
    data: null,
    languageData: null,
    currentDomain,
    latestArticles: null,
    pmArticles: null,
    isRedis: false,
  } as PageProps

  // TODO: check for contentful `!res` in isErrorPage (and also use the data in serverPropsCached)
  // Return error page if the URL belong to the error page
  const isErrorPage = checkIsErrorPage(ctx.req.url)
  if (isErrorPage) {
    return {
      props: {
        status: ctx.req.url?.substring(1, 4),
        currentDomain,
      },
    }
  }

  const serverPropsCached = JSON.parse(await getServerPropsCached(combinedPageSlug))
  if (serverPropsCached) {
    return {
      props: {
        ...(JSON.parse(JSON.stringify(serverPropsCached))),
        isRedis: true
      } 
    }
  }

  basePageProps.data = await getCategoryContent(isPreviewMode, categorySlug, DOMAIN_LOCALES[currentDomain]?.code)
  const res = basePageProps.data

  if (!res) {
    return {
      props: {
        status: 404,
        currentDomain,
      },
    }
  }
  
  basePageProps.languageData = await getCategorySlugs(isPreviewMode, categorySlug, currentDomain, res?.sys?.id)
  basePageProps.locale = DOMAIN_LOCALES[currentDomain]?.code
  basePageProps.lang = articleLang

  // `+ 1` is for the featured article
  const ARTICLES_LIMIT
    = FIRST_THUMBNAIL_LIST_COUNT_DESKTOP + SECOND_THUMBNAIL_LIST_COUNT_DESKTOP + 1

  if (pageSlug === '/') {
    basePageProps.latestArticles = !!res && (await getLatestArticles({
      isPreviewMode,
      locale: DOMAIN_LOCALES[currentDomain]?.code,
      lang: articleLang,
    }))
    basePageProps.pmArticles = await getPmArticles({
      isPreviewMode,
      locale: DOMAIN_LOCALES[currentDomain]?.code,
      limit: NUM_OF_PM_ARTICLES,
    })
  } else {
    switch (categoryPageId) {
      case CategoryTypeKeys.Topic:
        // get latest topic-related (do not care about language yet)
        basePageProps.latestArticles = !!res && (await getLatestTopicTagRelatedArticles({
          isPreviewMode,
          locale: DOMAIN_LOCALES[currentDomain]?.code,
          type: res.type,
          pageSlug: res.pageSlug,
        }))
        break
      default:
        // get latest tag-related (do not care about language yet)
        basePageProps.latestArticles = !!res && (await getLatestTopicTagRelatedArticles({
          isPreviewMode,
          locale: DOMAIN_LOCALES[currentDomain]?.code,
          type: res.type,
          pageSlug: res.pageSlug,
        }))
        break
    }
  }

  basePageProps.latestArticlesLength = basePageProps.latestArticles?.length
  basePageProps.latestArticles = basePageProps.latestArticles.slice(0, ARTICLES_LIMIT)

  // Needs page data to cache (others can be optional, but can keep it here just in case)
  if (!isPreviewMode && basePageProps.data && basePageProps.languageData?.length) {
    setUrlById(basePageProps.data.sys.id, combinedPageSlug)
    cacheServerProps(combinedPageSlug, basePageProps)
  }

  return {
    props: {
      ...(JSON.parse(JSON.stringify(basePageProps))),
    },
  }
}

export default CategoryPage
